import { logAdminAction, AdminActionType } from './auditLogger';
import { updateManifestAsnsCoupa, createSiteReceiptCoupa } from '../common/helperFunctions/coupa';
import { UpdateAsnProps } from '../api/postQM';
import { mapToReceiptedItems } from '../pages/receive-manifest/details/detail.mapping';
import { AsnStatus, ManifestPackageStatus, ManifestStatus, SsccStatus } from '../common/status';
import { AlertColor } from '@mui/material';
import { AlertStyles } from '../hooks/useAlert';
import { CurrentUserProps } from '../hooks/useCurrentUser';
import { variousAsn } from '../common/constants';
import { ManifestProps, TripSheetProps } from '../datastore/tripSheets';
import { DatastoreContextProps } from '../hooks/useDatastore';
import { updateAsnStatus } from '../common/helperFunctions/sscc';
import { Manifest, SelectedManifest } from '../pages/admin-panel/components/ManifestManagement';
import dayjs from 'dayjs';
import { flatMapPackagesAndAsnLines, updateSsccTimeBasedOnStatus, extractASNsFromManifest } from './utils';

const onSaveChanges = async ({
  manifest,
  tripData,
  alert,
  store
}: {
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  manifest: Manifest;
  tripData: TripSheetProps | undefined;
  store: DatastoreContextProps;
}) => {
  if (!manifest || !tripData) {
    alert('No manifest data or trip data', 'error');
    return;
  }

  try {
    let updatedManifest: ManifestProps = { ...manifest };

    const updatedLegs = tripData.legs.map((leg) => {
      const manifestIndex = leg.manifests.findIndex((m) => m.id === updatedManifest.id);
      if (manifestIndex !== -1) {
        leg.manifests[manifestIndex] = updatedManifest;
      }
      return leg;
    });

    const updatedTripSheet = {
      ...tripData,
      legs: updatedLegs
    };

    await store.tripSheets.update(updatedTripSheet);
    alert('Changes saved successfully', 'success');
  } catch (error) {
    console.error(error);
    alert('Failed to save changes', 'error');
  }
};

const handleSsccAsnStatusUpdate = async ({
  tripData,
  alert,
  store
}: {
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  tripData: TripSheetProps | undefined;
  store: DatastoreContextProps;
}) => {
  if (!tripData) {
    alert('No trip data', 'error');
    return;
  }
  for (const leg of tripData.legs) {
    for (const manifest of leg.manifests) {
      for (const sscc of manifest.ssccs) {
        const foundSscc = await store.ssccs.get(sscc.ssccId);
        if (!foundSscc) {
          console.info(`SSCC with an ID ${sscc.ssccId} not found - ignoring asn update`);
          continue;
        }
        updateAsnStatus(foundSscc, AsnStatus.available);
        await store.ssccs.update(foundSscc);
      }
    }
  }
};

export const markManifestCompleted = async ({
  selectedManifest,
  actionReason,
  tripData,
  alert,
  store,
  user,
  onlySendToCoupa
}: {
  selectedManifest: SelectedManifest;
  actionReason: string;
  tripData: TripSheetProps | undefined;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
  onlySendToCoupa: boolean;
}): Promise<boolean> => {
  const rawManifest = selectedManifest.manifest;

  if (!rawManifest) {
    alert('Cannot load the manifest. Please reload the page.', 'error');
    return false;
  }

  const beforeState = {
    manifestId: rawManifest.id,
    status: selectedManifest.tripDisplayStatus,
    tripId: selectedManifest.tripId,
    legId: selectedManifest.legId,
    asns: extractASNsFromManifest(rawManifest)
  };

  const manifest = {
    ...selectedManifest.manifest,
    ssccs: selectedManifest.manifest.ssccs.map((s) => {
      return {
        ...s,
        packages: s.packages.map((p) => {
          if (p.receivedStatus === ManifestPackageStatus.notReceived) {
            return {
              ...p,
              quantity: p.maxQuantity,
              receivedStatus: ManifestPackageStatus.received
            };
          }

          return p;
        })
      };
    })
  };

  try {
    alert('Submitting to Coupa', 'info');

    const reformattedManifest = {
      id: manifest.id,
      status: manifest.status,
      tripId: manifest.tripId,
      trailerId: manifest.trailerId,
      ssccs: manifest.ssccs
    };

    const receiptedItems = mapToReceiptedItems(reformattedManifest);

    for (const receiptedItem of receiptedItems) {
      const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
      if (receiptResponse.error) {
        alert('Error receipting items to coupa, please try again.', 'error');
        console.error(receiptResponse.error);
        return false;
      }
    }

    const uniqueAsns = [...new Set(receiptedItems.map((item) => item.asn))];

    const event: UpdateAsnProps = {
      asns: uniqueAsns.filter((asn): asn is string => asn !== undefined && asn !== null && asn !== variousAsn)
    };

    if (event.asns.length > 0) {
      await updateManifestAsnsCoupa(event);
    }

    manifest.status = ManifestStatus.completed;

    !onlySendToCoupa && alert('Saving changes', 'info');

    !onlySendToCoupa &&
      (await onSaveChanges({
        manifest,
        tripData,
        alert,
        store
      }));
    !onlySendToCoupa && (await handleSsccAsnStatusUpdate({ tripData, alert, store }));

    alert('Manifest submitted successfully', 'success');

    const afterState = {
      manifestId: manifest.id,
      status: manifest.status,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      asns: extractASNsFromManifest(rawManifest)
    };

    await logAdminAction({
      actionType: AdminActionType.MANIFEST_COMPLETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    console.error('Error submitting manifest: ', error);

    alert('Error submitting manifest. Please try again.', 'error');
    return false;
  }
};

export const addSSCCToManifest = async ({
  selectedManifest,
  selectedSSCCId,
  receiveManifest,
  selectedSSCCWeight,
  ssccWeight,
  tripSheets,
  addSSCCReason,
  alert,
  store,
  user
}: {
  selectedManifest: SelectedManifest | null;
  selectedSSCCId: string;
  receiveManifest: boolean;
  selectedSSCCWeight: string | undefined | null;
  ssccWeight: string | undefined;
  tripSheets: TripSheetProps[];
  addSSCCReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  if (!selectedManifest) {
    alert('Data error', 'error');
    return false;
  }

  try {
    const ssccData = await store.ssccs.get(selectedSSCCId);
    if (!ssccData) {
      alert('SSCC not found', 'error');
      return false;
    }

    // Get the trip data
    const tripData = await store.tripSheets.get(selectedManifest.tripId);
    if (!tripData) {
      alert('Trip not found', 'error');
      return false;
    }

    // Capture before state for audit
    const beforeState = {
      manifestId: selectedManifest?.manifest?.id,
      tripId: selectedManifest.tripId,
      ssccId: selectedSSCCId,
      ssccIds: selectedManifest.manifest.ssccs.map((sscc) => sscc.ssccId)
    };

    const updatedSSCC = updateSsccTimeBasedOnStatus({
      sscc: ssccData,
      status: selectedManifest.ssccStatus as SsccStatus,
      weight: selectedSSCCWeight ?? ssccWeight ?? '0'
    });

    updatedSSCC.receiptedEvents = updatedSSCC.receiptedEvents.map((event: any) => ({
      ...event,
      packages: event.packages.map((pkg: any) => ({
        ...pkg,
        status: selectedManifest.asnStatus,
        statusDate: dayjs()
      }))
    }));

    const newlyAddedSSCC = {
      ssccId: updatedSSCC.id,
      totalWeight: Number(updatedSSCC.weight),
      packages: flatMapPackagesAndAsnLines({
        originalSSCC: updatedSSCC,
        receiveManifest
      })
    };

    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((leg) => {
        if (leg.id === selectedManifest.legId) {
          return {
            ...leg,
            manifests: leg.manifests.map((m) => {
              if (m.id === selectedManifest.manifest.id) {
                return {
                  ...m,
                  ssccs: [...m.ssccs, ...[newlyAddedSSCC]]
                };
              }
              return m;
            })
          };
        }

        return leg;
      })
    };

    if (receiveManifest) {
      await markManifestCompleted({
        selectedManifest: { ...selectedManifest, manifest: { ...selectedManifest.manifest, ssccs: [newlyAddedSSCC] } },
        actionReason: '',
        alert,
        store,
        user,
        tripData: tripSheets.find((t) => t.id === selectedManifest.tripId),
        onlySendToCoupa: true
      });
    }

    await store.ssccs.update(updatedSSCC);
    await store.tripSheets.update(updatedTripData);

    // Capture after state for audit
    const afterState = {
      manifestId: selectedManifest?.manifest?.id,
      tripId: selectedManifest.tripId,
      ssccId: selectedSSCCId,
      ssccs: [...selectedManifest?.manifest?.ssccs.map((sscc) => sscc.ssccId), newlyAddedSSCC.ssccId]
    };

    // Log audit action
    await logAdminAction({
      actionType: AdminActionType.SSCC_ADD_TO_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: addSSCCReason
    });

    return true;
  } catch (error) {
    console.error('Failed to add SSCC to manifest:', error);
    return false;
  }
};

export const deleteManifest = async ({
  selectedManifest,
  actionReason,
  alert,
  store,
  user
}: {
  selectedManifest: SelectedManifest | null;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  if (!selectedManifest) {
    alert('error', 'error');
    return false;
  }

  const beforeState = {
    tripId: selectedManifest.tripId,
    legId: selectedManifest.legId,
    manifestId: selectedManifest.manifest.id
  };

  try {
    const tripData = await store.tripSheets.get(selectedManifest.tripId);
    if (!tripData) {
      alert('Trip not found', 'error');
      return false;
    }

    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((leg) => {
        if (leg.id === selectedManifest.legId) {
          return {
            ...leg,
            manifests: leg.manifests.filter(({ id }) => id !== selectedManifest.manifest.id)
          };
        }

        return leg;
      })
    };

    await store.tripSheets.update(updatedTripData);

    const afterState = {
      manifestId: selectedManifest.manifest.id,
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      deleted: true
    };

    await logAdminAction({
      actionType: AdminActionType.MANIFEST_DELETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    console.error('Failed to delete manifest:', error);
    return false;
  }
};

export const deleteSSCC = async ({
  selectedManifest,
  actionReason,
  alert,
  store,
  user
}: {
  selectedManifest: SelectedManifest | null;
  actionReason: string;
  alert: (_message: string, _severity: AlertColor, _styles?: AlertStyles) => void;
  store: DatastoreContextProps;
  user: CurrentUserProps;
}): Promise<boolean> => {
  if (!selectedManifest || !selectedManifest.ssccId) {
    alert('Data error', 'error');
    return false;
  }

  const beforeState = {
    tripId: selectedManifest.tripId,
    legId: selectedManifest.legId,
    manifestId: selectedManifest.manifest.id,
    ssccs: selectedManifest.manifest.ssccs.map((sscc) => sscc.ssccId)
  };

  try {
    const tripData = await store.tripSheets.get(selectedManifest.tripId);
    if (!tripData) {
      alert('Trip not found', 'error');
      return false;
    }

    const updatedTripData = {
      ...tripData,
      legs: tripData.legs.map((leg) => {
        if (leg.id === selectedManifest.legId) {
          return {
            ...leg,
            manifests: leg.manifests.map((m) => {
              return {
                ...m,
                ssccs: m.ssccs.filter((sscc) => sscc.ssccId !== selectedManifest.ssccId)
              };
            })
          };
        }

        return leg;
      })
    };

    await store.tripSheets.update(updatedTripData);

    const afterState = {
      tripId: selectedManifest.tripId,
      legId: selectedManifest.legId,
      manifestId: selectedManifest.manifest.id,
      ssccs: selectedManifest.manifest.ssccs
        .map((sscc) => sscc.ssccId)
        .filter((ssccId) => ssccId !== selectedManifest.ssccId),
      deleted: true
    };

    await logAdminAction({
      actionType: AdminActionType.SSCC_REMOVE_FROM_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy: user.email,
      reason: actionReason
    });

    return true;
  } catch (error) {
    console.error('Failed to delete manifest:', error);
    return false;
  }
};
